import { normalizeInternalPath } from "./redirects";

/** Set by /auth/callback after recovery email link; allows password update without retyping current password. */
export const PASSWORD_RECOVERY_COOKIE = "vortixy_pwd_recovery";

function normalizeOrigin(origin: string): string {
  return origin.replace(/\/$/, "");
}

export function normalizeRecoveryNextPath(candidate: string | null | undefined): string {
  return normalizeInternalPath(candidate, "/chat");
}

export function buildPasswordRecoveryRedirectTo(origin: string, next?: string): string {
  const safeNext = normalizeRecoveryNextPath(next);
  const updatePasswordPath = `/update-password?next=${encodeURIComponent(safeNext)}`;
  return `${normalizeOrigin(origin)}/auth/callback?next=${encodeURIComponent(updatePasswordPath)}`;
}

export type PasswordStrengthIssue =
  "too_short" | "missing_upper" | "missing_lower" | "missing_digit";

export interface PasswordStrengthResult {
  ok: boolean;
  issues: PasswordStrengthIssue[];
  hasMinLength: boolean;
  hasUpper: boolean;
  hasLower: boolean;
  hasDigit: boolean;
}

/** Concrete rule checks for signup / recovery / password change. */
export function getPasswordStrength(password: string): PasswordStrengthResult {
  const hasMinLength = password.length >= 8;
  const hasUpper = /[A-Z]/.test(password);
  const hasLower = /[a-z]/.test(password);
  const hasDigit = /\d/.test(password);
  const issues: PasswordStrengthIssue[] = [];
  if (!hasMinLength) issues.push("too_short");
  if (!hasUpper) issues.push("missing_upper");
  if (!hasLower) issues.push("missing_lower");
  if (!hasDigit) issues.push("missing_digit");
  return {
    ok: issues.length === 0,
    issues,
    hasMinLength,
    hasUpper,
    hasLower,
    hasDigit,
  };
}

export function isStrongRecoveryPassword(password: string): boolean {
  return getPasswordStrength(password).ok;
}

/** First failing rule as a stable issue id (for i18n mapping). */
export function getFirstPasswordIssue(password: string): PasswordStrengthIssue | null {
  const { issues } = getPasswordStrength(password);
  return issues[0] ?? null;
}
